import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  Clock, 
  CreditCard, 
  ArrowRight,
  Plus,
  Video,
  MapPin,
  FlaskConical,
  Trash2,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [pendingDrafts, setPendingDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoStatus, setDemoStatus] = useState({ has_demo_data: false, demo_patients_count: 0, demo_sessions_count: 0 });
  const [demoLoading, setDemoLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchDemoStatus();
  }, []);

  const fetchDemoStatus = async () => {
    try {
      const response = await fetch(`${API}/demo/status`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setDemoStatus(await response.json());
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Demo status error:', error);
    }
  };

  const handleGenerateDemo = async () => {
    setDemoLoading(true);
    try {
      const response = await fetch(`${API}/demo/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        if (result.created) {
          toast.success(`Created ${result.patients_count} demo patients and ${result.sessions_count} demo sessions`);
          fetchDashboardData();
          fetchDemoStatus();
        } else {
          toast.info('Demo data already exists');
        }
      }
    } catch (error) {
      toast.error('Failed to generate demo data');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleClearDemo = async () => {
    setDemoLoading(true);
    try {
      const response = await fetch(`${API}/demo/clear`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Cleared ${result.patients_deleted} demo patients and ${result.sessions_deleted} demo sessions`);
        fetchDashboardData();
        fetchDemoStatus();
      }
    } catch (error) {
      toast.error('Failed to clear demo data');
    } finally {
      setDemoLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, sessionsRes, draftsRes] = await Promise.all([
        fetch(`${API}/dashboard/stats`, { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API}/sessions/today`, { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API}/drafts/pending`, { headers: getAuthHeaders(), credentials: 'include' })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (sessionsRes.ok) {
        setTodaySessions(await sessionsRes.json());
      }
      if (draftsRes.ok) {
        setPendingDrafts(await draftsRes.json());
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: "Today's Sessions", 
      value: stats?.today_sessions || 0,
      subtitle: `${stats?.confirmed_today || 0} confirmed, ${stats?.tentative_today || 0} tentative`,
      icon: Calendar,
      color: 'text-primary'
    },
    { 
      title: "This Week", 
      value: stats?.this_week_total || 0,
      subtitle: `${stats?.this_week_online || 0} online, ${stats?.this_week_in_person || 0} in-person`,
      icon: Calendar,
      color: 'text-blue-600'
    },
    { 
      title: 'Follow-up Due', 
      value: stats?.follow_up_due || 0,
      subtitle: 'No session in 7 days',
      icon: Clock,
      color: 'text-secondary-foreground'
    },
    { 
      title: 'Unpaid Sessions', 
      value: stats?.unpaid_sessions || 0,
      subtitle: 'Awaiting payment',
      icon: CreditCard,
      color: 'text-amber-600'
    }
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading dashboard...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Demo Data Banner */}
        {demoStatus.has_demo_data && (
          <Card className="border-amber-300 bg-amber-50/50 border-dashed">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-amber-100">
                    <FlaskConical className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-amber-900">Demo Data Active</h3>
                    <p className="text-sm text-amber-700">
                      {demoStatus.demo_patients_count} sample patients, {demoStatus.demo_sessions_count} sample sessions
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={handleClearDemo}
                  disabled={demoLoading}
                  data-testid="clear-demo-btn"
                >
                  {demoLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-1" />
                  )}
                  Clear Demo Data
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generate Demo Data (only if no patients exist) */}
        {!demoStatus.has_demo_data && stats?.total_patients === 0 && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-primary/10">
                    <FlaskConical className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">New to ClarityStack?</h3>
                    <p className="text-sm text-muted-foreground">
                      Load demo data to explore the dashboard with sample patients and sessions
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="rounded-lg bg-primary hover:bg-primary/90"
                  onClick={handleGenerateDemo}
                  disabled={demoLoading}
                  data-testid="generate-demo-btn"
                >
                  {demoLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <FlaskConical className="h-4 w-4 mr-1" />
                  )}
                  Load Demo Data
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-foreground"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Good {getTimeOfDay()}, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Here's what's happening with your practice today
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              className="rounded-xl bg-primary hover:bg-primary/90"
              onClick={() => navigate('/patients')}
              data-testid="new-patient-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Patient
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card 
              key={`stat-${stat.title}`} 
              className="border-border hover:border-secondary/50 transition-all duration-200 hover:shadow-md"
              data-testid={`stat-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className="text-3xl font-bold text-foreground mt-1">{stat.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-xl bg-muted ${stat.color}`}>
                    <stat.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Today's Sessions */}
          <div className="lg:col-span-2">
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>
                  Today's Sessions
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-primary"
                  onClick={() => navigate('/sessions')}
                >
                  View all
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {todaySessions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No sessions scheduled for today</p>
                    <Button 
                      variant="link" 
                      className="mt-2 text-primary"
                      onClick={() => navigate('/sessions')}
                    >
                      Schedule a session
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {todaySessions.slice(0, 5).map((session) => (
                      <div 
                        key={session.session_id}
                        className={`flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer ${session.is_demo ? 'border border-dashed border-amber-300' : ''}`}
                        onClick={() => navigate(`/sessions/${session.session_id}`)}
                        data-testid={`session-${session.session_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${session.is_demo ? 'bg-amber-100' : 'bg-secondary/30'}`}>
                            <span className={`text-sm font-medium ${session.is_demo ? 'text-amber-700' : 'text-primary'}`}>
                              {session.patient_name?.charAt(0) || 'P'}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">{session.patient_name}</p>
                              {session.is_demo && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs py-0">
                                  Demo
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {session.scheduled_time}
                              <span className="mx-1">•</span>
                              {session.mode === 'video' ? (
                                <Video className="h-3 w-3" />
                              ) : (
                                <MapPin className="h-3 w-3" />
                              )}
                              {session.mode}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline"
                            className={session.status === 'confirmed' ? 'badge-confirmed' : 'badge-tentative'}
                          >
                            {session.status}
                          </Badge>
                          <Badge 
                            variant="outline"
                            className={session.payment_status === 'paid' ? 'badge-paid' : 'badge-pending'}
                          >
                            {session.payment_status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div>
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start rounded-xl"
                  onClick={() => navigate('/patients')}
                >
                  <Users className="h-4 w-4 mr-3" />
                  View All Patients
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start rounded-xl"
                  onClick={() => navigate('/sessions')}
                >
                  <Calendar className="h-4 w-4 mr-3" />
                  Manage Sessions
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

export default Dashboard;
